/*
 * SeriesEditor — compact per-series control strip below the chart. One row
 * per selected leaf, exposing render-type / colour / axis-group / visibility
 * / smoothing / thresholds toggles. Change events bubble up as partial
 * SeriesOptions patches so the parent can merge without a full re-emit.
 *
 * Kept intentionally dense: with 12+ series on a chart the editor otherwise
 * eats more vertical space than the chart itself.
 */

import type { LeafDto } from '../../api/types';
import type { AggregationName } from '../../api/history';
import type { SeriesLineStyle, SeriesOptions, SeriesRenderType } from './MultiSeriesChart';
import './SeriesEditor.css';

interface Row {
  leaf: LeafDto;
  options: SeriesOptions;
  /** Colour the chart draws the series in (override or palette). */
  colour: string;
  /** Calculations this series offers in mode "Berechnet", with labels. */
  calculations: { value: AggregationName; label: string }[];
  /** Calculation in effect. */
  calculation: AggregationName;
}

interface Props {
  rows: Row[];
  /** Mode "Berechnet": the calculation can be chosen. In "Rohdaten" every
   *  series shows its raw data and the column is inactive. */
  computed: boolean;
  onPatch(leafId: string, patch: Partial<SeriesOptions>): void;
  onRemove(leafId: string): void;
}

const RENDER_TYPES: { value: SeriesRenderType; label: string }[] = [
  { value: 'line', label: 'Linie' },
  { value: 'area', label: 'Fläche' },
  { value: 'bar', label: 'Balken' },
  { value: 'scatter', label: 'Punkt' },
];

const LINE_STYLES: { value: SeriesLineStyle; label: string }[] = [
  { value: 'solid',  label: '─── voll' },
  { value: 'dashed', label: '── strich' },
  { value: 'dotted', label: '··· punkt' },
];

export function SeriesEditor({ rows, computed, onPatch, onRemove }: Props) {
  if (rows.length === 0) {
    return (
      <div class="hv-series-editor__empty muted">
        Wähle Datenpunkte im Picker, um Serien anzuzeigen.
      </div>
    );
  }
  return (
    <div class="hv-series-editor" role="table" aria-label="Serien-Einstellungen">
      <div class="hv-series-editor__head" role="row">
        <div class="hv-series-editor__col hv-series-editor__col--vis" role="columnheader">Anz.</div>
        <div class="hv-series-editor__col hv-series-editor__col--colour" role="columnheader">Farbe</div>
        <div class="hv-series-editor__col hv-series-editor__col--name" role="columnheader">Serie</div>
        <div class="hv-series-editor__col hv-series-editor__col--calc" role="columnheader">Berechnung</div>
        <div class="hv-series-editor__col hv-series-editor__col--type" role="columnheader">Typ</div>
        <div class="hv-series-editor__col hv-series-editor__col--linestyle" role="columnheader">Linienart</div>
        <div class="hv-series-editor__col hv-series-editor__col--width" role="columnheader">Stärke</div>
        <div class="hv-series-editor__col hv-series-editor__col--axis" role="columnheader">Achse</div>
        <div class="hv-series-editor__col hv-series-editor__col--toggles" role="columnheader">Optionen</div>
        <div class="hv-series-editor__col hv-series-editor__col--action" role="columnheader" />
      </div>
      {rows.map((r) => {
        const visible = r.options.visible !== false;
        return (
          <div class="hv-series-editor__row" role="row" key={r.leaf.id}>
            <div class="hv-series-editor__col hv-series-editor__col--vis" role="cell">
              <input
                type="checkbox"
                checked={visible}
                onChange={(e) => onPatch(r.leaf.id, { visible: (e.currentTarget as HTMLInputElement).checked })}
                aria-label="Serie anzeigen"
              />
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--colour" role="cell">
              <input
                type="color"
                class="hv-series-editor__colour-input"
                value={r.colour}
                onChange={(e) => onPatch(r.leaf.id, { colour: (e.currentTarget as HTMLInputElement).value })}
                aria-label="Farbe"
              />
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--name" role="cell" title={r.leaf.symbol}>
              <span class="hv-series-editor__name">{r.leaf.displayName || r.leaf.symbol}</span>
              <span class="hv-series-editor__unit muted">{r.leaf.unit ?? ''}</span>
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--calc" role="cell">
              <select
                value={r.calculation}
                disabled={!computed || r.calculations.length < 2}
                onChange={(e) => onPatch(r.leaf.id, { calculation: (e.currentTarget as HTMLSelectElement).value as AggregationName })}
                aria-label="Berechnung"
                title={computed ? 'Was je Intervall gezeigt wird' : 'Nur in „Berechnet“ wirksam; in „Rohdaten“ zeigt jede Serie die Meldungen'}
              >
                {r.calculations.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--type" role="cell">
              <select
                // 'step' is gone from the list (22.10.2026): "Linie" already draws
                // CoV data as steps. Older presets holding it show as "Linie".
                value={r.options.renderType === 'step' ? 'line' : r.options.renderType}
                onChange={(e) => onPatch(r.leaf.id, { renderType: (e.currentTarget as HTMLSelectElement).value as SeriesRenderType })}
                aria-label="Render-Typ"
              >
                {RENDER_TYPES.map((rt) => (
                  <option key={rt.value} value={rt.value}>{rt.label}</option>
                ))}
              </select>
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--linestyle" role="cell">
              <select
                value={r.options.lineStyle ?? 'solid'}
                onChange={(e) => onPatch(r.leaf.id, { lineStyle: (e.currentTarget as HTMLSelectElement).value as SeriesLineStyle })}
                aria-label="Linienart"
                title="Linien-Typ (nur für Linie und Fläche wirksam)"
              >
                {LINE_STYLES.map((ls) => (
                  <option key={ls.value} value={ls.value}>{ls.label}</option>
                ))}
              </select>
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--width" role="cell">
              <input
                type="number"
                min={1}
                max={6}
                step={1}
                value={r.options.strokeWidth ?? 2}
                onChange={(e) => {
                  const raw = Number((e.currentTarget as HTMLInputElement).value);
                  const clamped = Number.isFinite(raw) ? Math.max(1, Math.min(6, Math.round(raw))) : 2;
                  onPatch(r.leaf.id, { strokeWidth: clamped });
                }}
                aria-label="Linienstärke"
                title="Linienstärke in px (1–6)"
              />
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--axis" role="cell">
              <input
                type="text"
                value={r.options.axisGroup ?? ''}
                placeholder={r.leaf.unit || 'auto'}
                onInput={(e) => onPatch(r.leaf.id, { axisGroup: (e.currentTarget as HTMLInputElement).value })}
                aria-label="Achsengruppe"
                title="Leer: eine Achse je Einheit. Gleicher Name bei mehreren Serien: gemeinsame Achse."
              />
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--toggles" role="cell">
              <label title="Nur Optik: weiche Kurve statt Treppe. Kurze Spitzen innerhalb eines Pixels entfallen; Werte und CSV bleiben exakt.">
                <input
                  type="checkbox"
                  checked={r.options.smooth === true}
                  onChange={(e) => onPatch(r.leaf.id, { smooth: (e.currentTarget as HTMLInputElement).checked })}
                />
                <span class="muted">glätten</span>
              </label>
              <label title="Färbt die Bereiche ober- und unterhalb der Warn- und Alarmgrenzen des Datenpunkts ein (gelb Warnung, rot Alarm). Ändert keine Werte.">
                <input
                  type="checkbox"
                  checked={r.options.showThresholds !== false}
                  onChange={(e) => onPatch(r.leaf.id, { showThresholds: (e.currentTarget as HTMLInputElement).checked })}
                />
                <span class="muted">Warn-/Alarmbereich</span>
              </label>
            </div>
            <div class="hv-series-editor__col hv-series-editor__col--action" role="cell">
              <button
                type="button"
                class="hv-btn hv-btn--sm hv-series-editor__remove"
                onClick={() => onRemove(r.leaf.id)}
                title="Serie entfernen"
                aria-label="Serie entfernen"
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
